import React, { useState, useEffect } from "react";
import { Divider, Form, Modal, Switch } from "antd";
import { CheckOutlined, PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import { t } from "i18next";
import { useDispatch, useSelector } from "react-redux";
import centralApi from "../../../../services/centralApi";
import useMessage from "hooks/useMessage";
import { getToken } from "redux/features/app/authTokenSlice";
import { API_ENDPOINTS } from "utils/constants";
import Spinner from "../../../../components/Spinner/Spinner";
import PrimaryButton from "../../../../components/Buttons/PrimaryButton/PrimaryButton";
import TextInput from "../../../../components/Inputs/TextInput/TextInput";
import Selector from "../../../../components/Selector/Selector";
import sendgrid from "images/SendGrid.jpg";
import netcore from "images/netcore.png";
import aws from "images/AWSSES.png";
import gupshup from "images/gupshup.png";
import smtp from "images/smtp.png";
import imimobile from "images/imimobile.jpeg";
import route from "images/routemobilejpg.webp";
import karix from "images/karix.jpeg";
import twilio from "images/twilio.png";
import sinch from "images/sinch.png"
import DynamicSenderRouting from "./components/DynamicRouting/DynamicRouting";
import "./Partner.css";
import InnerNav from "components/InnerNav/InnerNav";
import { CampaignChannelTypeEnum } from "utils/enums";

// ── Partner catalogue ─────────────────────────────────────────────────────────

const PARTNER_CATALOGUE = [
  { name: "Twilio Sendgrid", icon: sendgrid, channels: [CampaignChannelTypeEnum.EMAIL] },
  { name: "Netcore", icon: netcore, channels: [CampaignChannelTypeEnum.EMAIL] },
  { name: "AWS SES", icon: aws, channels: [CampaignChannelTypeEnum.EMAIL] },
  { name: "Gupshup", icon: gupshup, channels: [CampaignChannelTypeEnum.EMAIL] },
  { name: "SMTP", icon: smtp, channels: [CampaignChannelTypeEnum.EMAIL] },
  { name: "IMIMobile", icon: imimobile, channels: [CampaignChannelTypeEnum.SMS, CampaignChannelTypeEnum.WHATSAPP] },
  { name: "Route Mobile", icon: route, channels: [CampaignChannelTypeEnum.SMS, CampaignChannelTypeEnum.WHATSAPP] },
  { name: "Karix", icon: karix, channels: [CampaignChannelTypeEnum.SMS, CampaignChannelTypeEnum.WHATSAPP] },
  { name: "Twilio", icon: twilio, channels: [CampaignChannelTypeEnum.SMS, CampaignChannelTypeEnum.WHATSAPP] },
  { name: "Gupshup", icon: gupshup, channels: [CampaignChannelTypeEnum.SMS, CampaignChannelTypeEnum.WHATSAPP] },
  { name: "Sinch", icon: sinch, channels: [CampaignChannelTypeEnum.SMS] },

];

const CHANNEL_TABS = [
  { key: CampaignChannelTypeEnum.EMAIL, label: "Email" },
  { key:CampaignChannelTypeEnum.SMS, label: "SMS" },
  { key: CampaignChannelTypeEnum.WHATSAPP, label: "WhatsApp" },
];

// ── Component ─────────────────────────────────────────────────────────────────

const Partner = () => {
  const [activePage, setActivePage] = useState("partners");
  const [activeChannel, setActiveChannel] = useState("EMAIL");
  const [selectedPartners, setSelectedPartners] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  const [customName, setCustomName] = useState("");
  const [customChannel, setCustomChannel] = useState("EMAIL");
  const [customIcon, setCustomIcon] = useState("");
  const [customDefault, setCustomDefault] = useState(false);

  const { showError, showSuccess } = useMessage();
  const dispatch: any = useDispatch();
  const loginUser: any = useSelector((state: any) => state.loginUser);
  const activeApp: any = useSelector((state: any) => state.activeApp);
  const appId = activeApp.appId;
  const api_key = loginUser.data.api_key;

  const isSelected = (name: string, channel: string) =>
    selectedPartners.some((p) => p.partnerName === name && p.channel === channel);

  const handleGridCardClick = (def: any) => {
    if (isSelected(def.name, activeChannel)) {
      setSelectedPartners((prev) =>
        prev.filter((p) => !(p.partnerName === def.name && p.channel === activeChannel))
      );
    } else {
      setSelectedPartners((prev) => [
        ...prev,
        { partnerName: def.name, channel: activeChannel, default: false, icon: def.icon },
      ]);
    }
  };

  const handleDefaultToggle = (partnerName: string, channel: string) => {
    setSelectedPartners((prev) =>
      prev.map((p) => {
        if (p.partnerName === partnerName && p.channel === channel) {
          return { ...p, default: !p.default };
        }
        if (p.channel === channel && p.default) {
          return { ...p, default: false };
        }
        return p;
      })
    );
  };

  const handleRemove = (partnerName: string, channel: string) => {
    setSelectedPartners((prev) =>
      prev.filter((p) => !(p.partnerName === partnerName && p.channel === channel))
    );
  };

  const openCustomModal = () => {
    setCustomName("");
    setCustomChannel(activeChannel);
    setCustomIcon("");
    setCustomDefault(false);
    setModalOpen(true);
  };

  const handleIconUpload = (e: any) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setCustomIcon(ev.target?.result);
    reader.readAsDataURL(file);
  };

  const handleCustomSave = () => {
    if (!customName.trim()) {
      showError("Partner name is required.");
      return;
    }
    const newPartner = {
      partnerName: customName.trim(),
      channel: customChannel,
      default: customDefault,
      icon: customIcon,
    };
    setSelectedPartners((prev) => {
      const base = customDefault
        ? prev.map((p) => (p.channel === customChannel ? { ...p, default: false } : p))
        : prev;
      return [...base, newPartner];
    });
    setModalOpen(false);
  };

  const onFormSubmit = async () => {
    try {
      setLoading(true);
      const token = await dispatch(getToken()).unwrap();
      await centralApi(
        "POST",
        API_ENDPOINTS.SAVE_PARTNER_INFO,
        {
          args: JSON.stringify({ app_id: appId, partnerData: selectedPartners }),
          app_id: appId,
          api_key,
          authToken: token,
        },
        null
      );
      showSuccess(t("sucessfullySavedLabel"));
      await getPartnerInfo();
    } catch {
      showError(t("somethingWrongLabel"));
    } finally {
      setLoading(false);
    }
  };

  const getPartnerInfo = async () => {
    try {
      setLoading(true);
      const data = await centralApi(
        "GET",
        API_ENDPOINTS.GET_PARTNER_INFO,
        null,
        { args: JSON.stringify({ app_id: appId }), app_id: appId, api_key }
      );
      setSelectedPartners(data?.partnerInfo?.partnerData || []);
    } catch {
      showError(t("somethingWrongLabel"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getPartnerInfo();
    dispatch(getToken());
  }, [appId]);

  const visiblePartners = PARTNER_CATALOGUE.filter((p) => p.channels.includes(activeChannel));
  const configuredForChannel = selectedPartners.filter((p) => p.channel === activeChannel);

  return (
    <div className="partner-setting-container">
      <div className="mt-4 mb-4">
        <InnerNav
          theme="light"
          selected={activePage}
          onTabClick={(tab: any) => setActivePage(tab.key)}
          menu={[
            { label: t("partnerSettingsLabel"), key: "partners" },
            { label: t("dynamicSenderRoutingLabel"), key: "routing" },
          ]}
        />

        <Divider className="partner-setting-custom-divider" />

        {activePage === "partners" && (
          <>
            <h6 className="partner-setting-container-heading">{t("addOrRemovePartnerLabel")}</h6>
            <p className="partner-setting-form-headings">{t("partnerSettingText")}</p>
            <Divider className="partner-setting-custom-divider" />

            {loading ? (
              <Spinner />
            ) : (
              <>
                <div className="partner-channel-tabs">
                  {CHANNEL_TABS.map((tab) => (
                    <button
                      key={tab.key}
                      className={`partner-channel-tab${activeChannel === tab.key ? " active" : ""}`}
                      onClick={() => setActiveChannel(tab.key)}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>

                <div className="partner-grid">
                  {visiblePartners.map((def) => {
                    const selected = isSelected(def.name, activeChannel);
                    return (
                      <div
                        key={`${def.name}-${activeChannel}`}
                        className={`partner-grid-card${selected ? " selected" : ""}`}
                        onClick={() => handleGridCardClick(def)}
                        title={def.name}
                      >
                        {selected && (
                          <div className="partner-grid-check">
                            <CheckOutlined />
                          </div>
                        )}
                        <div className="partner-grid-icon-wrap">
                          <img src={def.icon} alt={def.name} className="partner-grid-icon" />
                        </div>
                        <span className="partner-grid-label">{def.name}</span>
                      </div>
                    );
                  })}

                  <div
                    className="partner-grid-card partner-grid-add-card"
                    onClick={openCustomModal}
                    title="Add Custom Partner"
                  >
                    <div className="partner-grid-icon-wrap partner-grid-add-icon">
                      <PlusOutlined />
                    </div>
                    <span className="partner-grid-label add-custom">Add Custom</span>
                  </div>
                </div>

                {configuredForChannel.length > 0 && (
                  <div className="partner-configured-section">
                    <h6 className="partner-configured-heading">
                      Configured —{" "}
                      {CHANNEL_TABS.find((tab) => tab.key === activeChannel)?.label}
                    </h6>
                    <div className="partner-configured-list">
                      <div className="partner-configured-row partner-configured-header">
                        <span>Icon</span>
                        <span>Partner Name</span>
                        <span className="text-center d-block">Set as Default</span>
                        <span />
                      </div>
                      {configuredForChannel.map((p) => (
                        <div key={`${p.partnerName}-${p.channel}`} className="partner-configured-row">
                          <div className="partner-configured-icon-wrap">
                            {p.icon ? (
                              <img src={p.icon} alt={p.partnerName} className="partner-configured-icon" />
                            ) : (
                              <div className="partner-configured-icon-placeholder">
                                {p.partnerName.charAt(0).toUpperCase()}
                              </div>
                            )}
                          </div>
                          <span className="partner-configured-name">{p.partnerName}</span>
                          <div className="d-flex justify-content-center">
                            <Switch
                              size="small"
                              checked={p.default}
                              onChange={() => handleDefaultToggle(p.partnerName, p.channel)}
                            />
                          </div>
                          <button
                            className="partner-configured-remove"
                            onClick={() => handleRemove(p.partnerName, p.channel)}
                            title="Remove"
                          >
                            <DeleteOutlined />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {selectedPartners.length > 0 && (
                  <Form.Item className="mt-3">
                    <PrimaryButton
                      size="middle"
                      type="primary"
                      onClick={onFormSubmit}
                      className="border-0 btn-custom-width"
                    >
                      {t("saveLabel")}
                    </PrimaryButton>
                  </Form.Item>
                )}
              </>
            )}
          </>
        )}

        {activePage === "routing" && <DynamicSenderRouting configuredPartners={selectedPartners} />}
      </div>

      <Modal
        title="Add Custom Partner"
        open={modalOpen}
        onOk={handleCustomSave}
        onCancel={() => setModalOpen(false)}
        okText="Add Partner"
        destroyOnClose
      >
        <div className="partner-custom-modal-body">
          <div className="partner-custom-field">
            <label>{t("partnerNameLabel")} *</label>
            <TextInput
              value={customName}
              onChange={(e: any) => setCustomName(e.target.value)}
              maxLength={100}
              placeholder="e.g. My Email Provider"
            />
          </div>

          <div className="partner-custom-field">
            <label>Channel</label>
            <Selector
              value={customChannel}
              onChange={(val: any) => setCustomChannel(val)}
              size="middle"
              className="w-100"
              options={[
                { value: CampaignChannelTypeEnum.EMAIL, label: "Email" },
                { value: CampaignChannelTypeEnum.SMS, label: "SMS" },
                { value: CampaignChannelTypeEnum.WHATSAPP, label: "WhatsApp" },
              ]}
            />
          </div>

          <div className="partner-custom-field partner-custom-default-row">
            <label>Set as Default</label>
            <Switch size="small" checked={customDefault} onChange={(v: any) => setCustomDefault(v)} />
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Partner;